import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { useLanguage } from "../../../context/LanguageContext";
import { updateProfile } from "../../../services/profileService";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getAllJobCategories } from "../../../services/jobCategorieService";
import LocationSelector from "../../../components/LocationSelector";

const RightSideContent = ({ data }) => {
  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const [activeTab, setActiveTab] = useState("1");
  const [skillsInput, setSkillsInput] = useState("");
  const genders = [
    {
      value: "MALE",
      label: "Male",
      labelPt: "Masculino",
    },
    {
      value: "FEMALE",
      label: "Female",
      labelPt: "Feminino",
    },
    {
      value: "UNSPECIFIED",
      label: "Not specified",
      labelPt: "Não especificado",
    },
  ];
  const languages = [
    {
      value: "PORTUGUESE",
      label: "Portuguese",
      labelPt: "Português",
    },
    {
      value: "ENGLISH",
      label: "English",
      labelPt: "Inglês",
    },
  ];
  const ProficiencyLevel = {
    BASIC: "BASIC",
    INTERMEDIATE: "INTERMEDIATE",
    ADVANCED: "ADVANCED",
    NATIVE: "NATIVE",
  };
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    gender: "",
    birthDate: "",
    linkedin: "",
    address: "",
    yearsOfExperience: 0,
    desiredJobCategory: "",
    professionalSummary: "",
    cvLanguage: "",
    skills: [],
    languages: [],
    educations: [],
    experiences: [],
    cvs: [],
  });
  const [cvFile, setCvFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const token = localStorage.getItem("authToken");
  const requiredFields = [
    "firstName",
    "lastName",
    "birthDate",
    "yearsOfExperience",
    "gender",
    "desiredJobCategory",
  ];

  const normalizeText = (value) => String(value || "").trim();
  const hasCv = Boolean(cvFile) || Boolean(formData.cvs?.length);

  useEffect(() => {
    const savedTab = localStorage.getItem("activeTab");
    if (savedTab) {
      setActiveTab(savedTab);
    }

    if (data) {
      const skillsString =
        data.skills?.map((skill) => skill.name).join(", ") || "";

      setFormData({
        firstName: data.user?.firstName || "",
        lastName: data.user?.lastName || "",
        email: data.user?.email || "",
        gender: data.user?.gender || "",
        phone: data.phone || "",
        linkedin: data.linkedin || "",
        birthDate: data.birthDate || "",
        address: data.address || "",
        yearsOfExperience: data.yearsOfExperience || 0,
        desiredJobCategory: data.desiredJobCategory || "",
        country: data.country || "",
        state: data.state || "",
        city: data.city || "",
        cvLanguage: data.cvs?.[0]?.language || "pt",
        professionalSummary: data.professionalSummary || "",
        skills: data.skills || [],
        languages: data.languages?.length
          ? [...data.languages]
          : [{ name: "", proficiencyLevel: "" }],
        cvs: data.cvs?.length
          ? [...data.cvs]
          : [{ filePath: "", language: "", isDefault: false }],
        educations: data.educations?.length
          ? [...data.educations]
          : [
              {
                degree: "",
                course: "",
                location: "",
                institution: "",
                description: "",
                startDate: "",
                endDate: "",
              },
            ],
        experiences: data.experiences?.length
          ? [...data.experiences]
          : [
              {
                company: "",
                position: "",
                location: "",
                duties: "",
                startDate: "",
                endDate: "",
              },
            ],
        trainings: data.trainings?.length
          ? [...data.trainings]
          : [
              {
                course: "",
                institution: "",
                description: "",
                location: "",
                startDate: "",
                endDate: "",
              },
            ],
      });

      setSkillsInput(skillsString);
    }
  }, [data]);

  const { mutate: updateProfileMutation, isLoading } = useMutation(
    ({ candidateId, payload, cvFile }) =>
      updateProfile(candidateId, payload, cvFile, token),
    {
      onSuccess: () => {
        toast.success(
          language === "pt"
            ? "Perfil atualizado com sucesso!"
            : "Profile updated successfully!"
        );

        queryClient.invalidateQueries(["profile", data.id]);
        handleTabChange("1");
      },
    }
  );

  const { data: categoriesData, isError } = useQuery({
    queryKey: ["categories"],
    queryFn: () => getAllJobCategories({}, token),
    keepPreviousData: true,
  });

  const validateField = (fieldName, value) => {
    const messages = {
      firstName:
        language === "pt"
          ? "O nome é obrigatório."
          : "First name is required.",
      lastName:
        language === "pt"
          ? "O sobrenome é obrigatório."
          : "Last name is required.",
      birthDate:
        language === "pt"
          ? "A data de nascimento é obrigatória."
          : "Birth date is required.",
      yearsOfExperience:
        language === "pt"
          ? "Informe os anos de experiência."
          : "Years of experience is required.",
      gender:
        language === "pt" ? "Selecione um gênero." : "Select a gender.",
      desiredJobCategory:
        language === "pt"
          ? "Selecione uma categoria desejada."
          : "Select a desired job category.",
    };

    if (fieldName === "yearsOfExperience") {
      if (value === "" || value === null || value === undefined) {
        return messages[fieldName];
      }

      if (Number(value) < 0) {
        return language === "pt"
          ? "Os anos de experiência não podem ser negativos."
          : "Years of experience cannot be negative.";
      }

      return "";
    }

    if (fieldName === "birthDate") {
      if (!normalizeText(value)) {
        return messages[fieldName];
      }

      const selectedDate = new Date(value);
      const today = new Date();
      if (selectedDate > today) {
        return language === "pt"
          ? "A data de nascimento não pode estar no futuro."
          : "Birth date cannot be in the future.";
      }
      return "";
    }

    return normalizeText(value) ? "" : messages[fieldName] || "";
  };

  const validateLanguages = () => {
    const hasInvalidLanguage = (formData.languages || []).some(
      (lang) => !normalizeText(lang.name) || !normalizeText(lang.proficiencyLevel)
    );

    if (hasInvalidLanguage) {
      return language === "pt"
        ? "Preencha idioma e nível para todos os itens de idioma."
        : "Fill in language and level for all language entries.";
    }

    return "";
  };

  const updateFieldError = (fieldName, value) => {
    const errorMessage = validateField(fieldName, value);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (errorMessage) {
        next[fieldName] = errorMessage;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

    if (touchedFields[fieldName] || submitAttempted) {
      updateFieldError(fieldName, value);
    }
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    updateFieldError(fieldName, formData[fieldName]);
  };

  const shouldShowFieldError = (fieldName) =>
    Boolean((touchedFields[fieldName] || submitAttempted) && fieldErrors[fieldName]);

  const validateForm = () => {
    const nextErrors = {};

    requiredFields.forEach((fieldName) => {
      const errorMessage = validateField(fieldName, formData[fieldName]);
      if (errorMessage) {
        nextErrors[fieldName] = errorMessage;
      }
    });

    if (!hasCv) {
      nextErrors.cvFile =
        language === "pt"
          ? "Adicione pelo menos um currículo para continuar."
          : "Add at least one resume to continue.";
    }

    const languagesError = validateLanguages();
    if (languagesError) {
      nextErrors.languages = languagesError;
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitAttempted(true);

    if (!validateForm()) {
      toast.error(
        language === "pt"
          ? "Preencha os campos obrigatórios antes de atualizar o perfil."
          : "Fill in required fields before updating your profile."
      );
      return;
    }

    const skillsArray = convertStringToArray(skillsInput);

    // Preparar os dados para envio
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      gender: formData.gender,
      yearsOfExperience: formData.yearsOfExperience,
      birthDate: formData.birthDate,
      professionalSummary: formData.professionalSummary,
      linkedin: formData.linkedin,
      address: formData.address,
      desiredJobCategory: formData.desiredJobCategory,
      nationality: formData.nationality,
      skills: skillsArray,
      languages: formData.languages,
      educations: formData.educations,
      experiences: formData.experiences,
      trainings: formData.trainings,
      cvLanguage: formData.cvLanguage,
    };

    // Chamar a mutação
    updateProfileMutation({
      candidateId: data.id,
      payload,
      cvFile,
    });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem("activeTab", tabId);
  };

  const handleAddEducation = () => {
    setFormData({
      ...formData,
      educations: [
        ...formData.educations,
        {
          degree: "",
          course: "",
          location: "",
          institution: "",
          description: "",
          startDate: "",
          endDate: "",
        },
      ],
    });
  };

  const handleAddExperience = () => {
    setFormData({
      ...formData,
      experiences: [
        ...formData.experiences,
        {
          company: "",
          position: "",
          location: "",
          duties: "",
          startDate: "",
          endDate: "",
        },
      ],
    });
  };

  const handleAddTraining = () => {
    setFormData({
      ...formData,
      trainings: [
        ...formData.trainings,
        {
          course: "",
          institution: "",
          description: "",
          location: "",
          startDate: "",
          endDate: "",
        },
      ],
    });
  };

  const handleAddLanguage = () => {
    setFormData((prev) => ({
      ...prev,
      languages: [...prev.languages, { name: "", proficiencyLevel: "BASIC" }],
    }));

    setTouchedFields((prev) => ({ ...prev, languages: true }));
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        language === "pt"
          ? "O arquivo excede o tamanho máximo de 2MB"
          : "File exceeds maximum size of 2MB",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Limpa o input de arquivo
      e.target.value = "";
      return;
    }

    setCvFile(file);

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.cvFile;
      return next;
    });
  };

  const handleExperienceChange = (index, field, value) => {
    const updatedExperiences = [...formData.experiences];
    updatedExperiences[index][field] = value;
    setFormData({ ...formData, experiences: updatedExperiences });
  };

  const handleEducationChange = (index, field, value) => {
    const updatedEducations = [...formData.educations];
    updatedEducations[index][field] = value;
    setFormData({ ...formData, educations: updatedEducations });
  };

  const handleTrainingChange = (index, field, value) => {
    const updatedTrainings = [...formData.trainings];
    updatedTrainings[index][field] = value;
    setFormData({ ...formData, trainings: updatedTrainings });
  };

  const handleLanguageChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedLanguages = [...prev.languages];
      updatedLanguages[index][field] = value;
      return { ...prev, languages: updatedLanguages };
    });

    setTouchedFields((prev) => ({ ...prev, languages: true }));
  };

  const handleLocationChange = (newLocation) => {
    setFormData((prev) => ({
      ...prev,
      ...newLocation,
    }));
  };

  const handleRemoveExperience = (index) => {
    if (formData.experiences.length > 1) {
      // Garante que pelo menos uma experiência permaneça
      const filteredExperiences = formData.experiences.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, experiences: filteredExperiences });
    }
  };

  const handleRemoveEducation = (index) => {
    if (formData.educations.length > 1) {
      // Garante que pelo menos uma educação permaneça
      const filteredEducations = formData.educations.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, educations: filteredEducations });
    }
  };

  const handleRemoveTraining = (index) => {
    if (formData.trainings.length > 1) {
      // Garante que pelo menos um treinamento permaneça
      const filteredTrainings = formData.trainings.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, trainings: filteredTrainings });
    }
  };

  const handleRemoveLanguage = (index) => {
    if (formData.languages.length <= 1) return;

    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index),
    }));

    setTouchedFields((prev) => ({ ...prev, languages: true }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Presente";
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const convertStringToArray = (itemString) => {
    return itemString
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item !== "")
      .map((item) => ({
        name: item,
      }));
  };

  const profileInsights = useMemo(() => {
    const requiredCompletionCount = requiredFields.filter(
      (fieldName) => !validateField(fieldName, formData[fieldName])
    ).length;

    const skillsCount = convertStringToArray(skillsInput).length;
    const summaryLength = normalizeText(formData.professionalSummary).length;
    const completeLanguagesCount = (formData.languages || []).filter(
      (lang) => normalizeText(lang.name) && normalizeText(lang.proficiencyLevel)
    ).length;
    const hasExperience = (formData.experiences || []).some(
      (item) => normalizeText(item.position) && normalizeText(item.company)
    );
    const hasEducation = (formData.educations || []).some(
      (item) => normalizeText(item.course) && normalizeText(item.institution)
    );

    const scoreParts = [
      (requiredCompletionCount / requiredFields.length) * 55,
      Math.min(summaryLength / 120, 1) * 15,
      Math.min(skillsCount / 5, 1) * 10,
      formData.linkedin ? 5 : 0,
      hasExperience ? 5 : 0,
      hasEducation ? 3 : 0,
      completeLanguagesCount > 0 ? 4 : 0,
      hasCv ? 3 : 0,
    ];

    const score = Math.round(scoreParts.reduce((acc, value) => acc + value, 0));

    let profileStrength =
      language === "pt" ? "Perfil fraco" : "Weak profile";
    let strengthColor = "danger";

    if (score >= 80) {
      profileStrength = language === "pt" ? "Perfil forte" : "Strong profile";
      strengthColor = "success";
    } else if (score >= 55) {
      profileStrength =
        language === "pt" ? "Perfil moderado" : "Moderate profile";
      strengthColor = "warning";
    }

    const sectionStatus = [
      {
        key: "basic",
        label: language === "pt" ? "Dados essenciais" : "Essential data",
        complete: requiredCompletionCount === requiredFields.length,
      },
      {
        key: "summary",
        label:
          language === "pt" ? "Resumo e competências" : "Summary and skills",
        complete: summaryLength >= 80 && skillsCount >= 3,
      },
      {
        key: "experience",
        label: language === "pt" ? "Experiência" : "Experience",
        complete: hasExperience,
      },
      {
        key: "documents",
        label: language === "pt" ? "Currículo" : "Resume",
        complete: hasCv,
      },
    ];

    const tips = [];

    if (summaryLength < 80) {
      tips.push(
        language === "pt"
          ? "Adicione uma descrição mais detalhada sobre a sua experiência profissional."
          : "Add a more detailed description about your professional experience."
      );
    }

    if (!data?.user?.imagePath) {
      tips.push(
        language === "pt"
          ? "Perfis com foto têm maior taxa de resposta."
          : "Profiles with a photo tend to receive higher response rates."
      );
    }

    if (skillsCount < 5) {
      tips.push(
        language === "pt"
          ? "Inclua competências técnicas relevantes para a vaga desejada."
          : "Include technical skills relevant to your target role."
      );
    }

    if (!formData.linkedin) {
      tips.push(
        language === "pt"
          ? "Adicionar o LinkedIn aumenta a credibilidade do perfil."
          : "Adding your LinkedIn improves profile credibility."
      );
    }

    if (!hasExperience) {
      tips.push(
        language === "pt"
          ? "Preencha pelo menos uma experiência profissional com cargo e empresa."
          : "Add at least one professional experience with position and company."
      );
    }

    return {
      score,
      profileStrength,
      strengthColor,
      completion: Math.min(score, 100),
      tips,
      sectionStatus,
    };
  }, [
    data?.user?.imagePath,
    formData,
    hasCv,
    language,
    requiredFields,
    skillsInput,
  ]);

  useEffect(() => {
    if (!(submitAttempted || touchedFields.languages)) {
      return;
    }

    const languageError = validateLanguages();
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (languageError) {
        next.languages = languageError;
      } else {
        delete next.languages;
      }
      return next;
    });
  }, [formData.languages, submitAttempted, touchedFields.languages, language]);

  useEffect(() => {
    if (!hasCv) {
      return;
    }

    setFieldErrors((prev) => {
      if (!prev.cvFile) {
        return prev;
      }

      const next = { ...prev };
      delete next.cvFile;
      return next;
    });
  }, [hasCv]);

  const getPortugueseProficiency = (level) => {
    switch (level) {
      case "BASIC":
        return "Básico";
      case "INTERMEDIATE":
        return "Intermediário";
      case "ADVANCED":
        return "Avançado";
      case "NATIVE":
        return "Nativo";
      default:
        return level;
    }
  };

  const renderEmptyMessage = (ptMessage, enMessage) => (
    <p className="text-muted mt-3 mb-0">
      {language === "pt" ? ptMessage : enMessage}
    </p>
  );

  return (
    <React.Fragment>
      <Col lg={8}>
        <Card className="profile-content-page mt-4 mt-lg-0">
          <Nav
            className="profile-content-nav nav-pills border-bottom mb-4"
            id="pills-tab"
            role="tablist"
          >
            <NavItem role="presentation">
              <NavLink
                to="#"
                className={classnames({ active: activeTab === "1" })}
                onClick={() => handleTabChange("1")}
                type="button"
              >
                {language === "pt" ? "Visão geral" : "Overview"}
              </NavLink>
            </NavItem>
            <NavItem role="presentation">
              <NavLink
                to="#"
                className={classnames({ active: activeTab === "2" })}
                onClick={() => handleTabChange("2")}
                type="button"
              >
                {language === "pt" ? "Editar Perfil" : "Edit Profile"}
              </NavLink>
            </NavItem>
          </Nav>

          <CardBody className="p-4">
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                {data.professionalSummary ? (
                  <div
                    className={
                      profileInsights.sectionStatus.find((s) => s.key === "basic")
                        ?.complete
                        ? ""
                        : "border border-warning rounded p-3"
                    }
                  >
                    <h5 className="fs-18 fw-bold">
                      {language === "pt" ? "Sobre Mim" : "About Me"}
                    </h5>
                    <p className="text-muted mt-4">
                      {data.professionalSummary}
                    </p>
                  </div>
                ) : (
                  <div>
                    <h5 className="fs-18 fw-bold">
                      {language === "pt" ? "Sobre Mim" : "About Me"}
                    </h5>
                    {renderEmptyMessage(
                      "Ainda não há um resumo profissional. Adicione um resumo para destacar seu perfil.",
                      "There is no professional summary yet. Add one to make your profile stand out."
                    )}
                  </div>
                )}

                <div className="candidate-education-details mt-4">
                  <h6 className="fs-18 fw-bold mb-0">
                    {language === "pt" ? "Educação" : "Education"}
                  </h6>
                  {data.educations?.length ? (
                    data.educations.map((education, index) => (
                      <div
                        className="candidate-education-content mt-4 d-flex"
                        key={index}
                      >
                        <div className="circle flex-shrink-0 bg-primary-subtle text-primary">
                          {education.course?.charAt(0).toUpperCase()}
                        </div>

                        <div className="ms-4">
                          <h6 className="fs-16 mb-1">
                            {education.course} - {education.institution}
                          </h6>

                          <p className="mb-2 text-muted">
                            {education.location} •{" "}
                            {formatDate(education.startDate)} -{" "}
                            {formatDate(education.endDate)}
                          </p>

                          {education.description && (
                            <p className="text-muted">{education.description}</p>
                          )}

                          {education.degree &&
                            education.degree !== education.course && (
                              <p className="text-muted small mt-1">
                                <strong>
                                  {language === "pt" ? "Grau" : "Degree"}:
                                </strong>{" "}
                                {education.degree}
                              </p>
                            )}
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyMessage(
                      "Ainda não há dados de educação.",
                      "There are no education details yet."
                    )
                  )}
                </div>

                <div className="candidate-education-details mt-4">
                  <h6 className="fs-18 fw-bold mb-0">
                    {language === "pt"
                      ? "Experiência Profissional"
                      : "Profissional Experience"}
                  </h6>
                  {data.experiences?.length ? (
                    data.experiences.map((experience, index) => (
                      <div
                        className="candidate-education-content mt-4 d-flex"
                        key={index}
                      >
                        <div className="circle flex-shrink-0 bg-primary-subtle text-primary">
                          {experience.position?.charAt(0).toUpperCase()}
                        </div>
                        <div className="ms-4">
                          <h6 className="fs-16 mb-1">{experience.position}</h6>
                          <p className="mb-2 text-muted">
                            {experience.company} • {experience.location} • (
                            {new Date(experience.startDate).getFullYear()} -{" "}
                            {experience.endDate
                              ? new Date(experience.endDate).getFullYear()
                              : "Atual"}
                            )
                          </p>
                          {experience.duties && (
                            <div className="text-muted">
                              <h6 className="fs-14 mb-1">
                                {language === "pt"
                                  ? "Principais atividades:"
                                  : "Main duties"}
                              </h6>
                              <p className="mb-0">{experience.duties}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyMessage(
                      "Ainda não há experiências profissionais cadastradas.",
                      "There are no professional experiences yet."
                    )
                  )}
                </div>

                <div className="candidate-education-details mt-4">
                  <h6 className="fs-18 fw-bold mb-0">
                    {language === "pt"
                      ? "Cursos e Certificações"
                      : "Courses and Certifications"}
                  </h6>
                  {data.trainings?.length ? (
                    data.trainings.map((training, index) => (
                      <div
                        className="candidate-education-content mt-4 d-flex"
                        key={index}
                      >
                        <div className="circle flex-shrink-0 bg-primary-subtle text-primary">
                          {training.course?.charAt(0).toUpperCase()}
                        </div>
                        <div className="ms-4">
                          <h6 className="fs-16 mb-1">{training.course}</h6>
                          <p className="mb-2 text-muted">
                            {training.institution} • {training.location} • (
                            {new Date(training.startDate).toLocaleDateString(
                              "pt-BR",
                              { month: "short", year: "numeric" }
                            )}{" "}
                            -{" "}
                            {training.endDate
                              ? new Date(training.endDate).toLocaleDateString(
                                  "pt-BR",
                                  { month: "short", year: "numeric" }
                                )
                              : language === "pt"
                              ? "Atual"
                              : "Current"}
                            )
                          </p>
                          {training.description && (
                            <p className="text-muted mb-0">
                              <strong>
                                {language === "pt" ? "Conteúdo" : "Content"}:
                              </strong>{" "}
                              {training.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyMessage(
                      "Ainda não há cursos ou certificações cadastrados.",
                      "There are no courses or certifications yet."
                    )
                  )}
                </div>

                <div className="mt-4">
                  <h5 className="fs-18 fw-bold">
                    {language === "pt" ? "Habilidades" : "Skills"}
                  </h5>
                  {data.skills?.length ? (
                    <ul className="job-detail-list list-unstyled mb-0 text-muted">
                      {data.skills.map((skill, index) => (
                        <li key={index}>
                          <i className="uil uil-circle"></i> {skill.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    renderEmptyMessage(
                      "Ainda não há habilidades cadastradas.",
                      "There are no skills listed yet."
                    )
                  )}
                </div>

                <div className="mt-4">
                  <h5 className="fs-18 fw-bold">
                    {language === "pt" ? "Idiomas falados" : "Spoken languages"}
                  </h5>
                </div>

                <div className="mt-0 d-flex flex-wrap align-items-start gap-1">
                  {data.languages?.length ? (
                    data.languages.map((language, index) => (
                      <span
                        className="badge fs-13 bg-success-subtle text-success mt-2"
                        key={index}
                      >
                        {language.name}
                      </span>
                    ))
                  ) : (
                    renderEmptyMessage(
                      "Ainda não há idiomas informados.",
                      "There are no spoken languages yet."
                    )
                  )}
                </div>
              </TabPane>

              <TabPane tabId="2">
                <Form onSubmit={handleSubmit}>
                  <div className="mb-4 p-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <h6 className="mb-0 fw-semibold">
                        {language === "pt"
                          ? "Progresso do perfil"
                          : "Profile progress"}
                      </h6>
                      <span className={`badge bg-${profileInsights.strengthColor}`}>
                        {profileInsights.profileStrength}
                      </span>
                    </div>

                    <div className="progress mt-3" style={{ height: "12px" }}>
                      <div
                        className={`progress-bar bg-${profileInsights.strengthColor}`}
                        role="progressbar"
                        style={{ width: `${profileInsights.completion}%` }}
                        aria-valuenow={profileInsights.completion}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        {profileInsights.completion}%
                      </div>
                    </div>

                    <div className="mt-3 small">
                      {profileInsights.sectionStatus.map((section) => (
                        <div
                          key={section.key}
                          className="d-flex justify-content-between align-items-center mb-1"
                        >
                          <span>{section.label}</span>
                          <span
                            className={`badge ${
                              section.complete ? "bg-success" : "bg-warning text-dark"
                            }`}
                          >
                            {section.complete
                              ? language === "pt"
                                ? "Completo"
                                : "Complete"
                              : language === "pt"
                              ? "Incompleto"
                              : "Incomplete"}
                          </span>
                        </div>
                      ))}
                    </div>

                    {profileInsights.tips.length > 0 && (
                      <div className="alert alert-info mt-3 mb-0 py-2 px-3">
                        <strong>
                          {language === "pt"
                            ? "Dicas para melhorar o perfil"
                            : "Tips to improve your profile"}
                        </strong>
                        <ul className="mb-0 mt-2 ps-3">
                          {profileInsights.tips.slice(0, 4).map((tip, index) => (
                            <li key={index}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <small className="text-muted d-block mt-3">
                      {language === "pt"
                        ? "Campos com * são obrigatórios"
                        : "Fields marked with * are required"}
                    </small>
                  </div>

                  <div>
                    <h5 className="fs-17 fw-semibold mb-3 mb-0">
                      {language === "pt" ? "Minha Conta" : "My Account"}
                    </h5>
                    <div className="text-center">
                      {/* <div className="mb-4 profile-user position-relative">
                        <img
                          src={imagePreview}
                          className="rounded-circle img-thumbnail profile-img"
                          id="profile-img"
                          alt={
                            language === "pt"
                              ? "Imagem do perfil"
                              : "Profile image"
                          }
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                          }}
                        />

                        <div className="p-0 rounded-circle profile-photo-edit">
                          <Input
                            id="profile-img-file-input"
                            type="file"
                            className="profile-img-file-input d-none"
                            onChange={handleImageChange}
                            accept="image/*"
                          />
                          <Label
                            htmlFor="profile-img-file-input"
                            className="profile-photo-edit avatar-xs cursor-pointer"
                            title={
                              language === "pt"
                                ? "Alterar imagem"
                                : "Change image"
                            }
                          >
                            <i className="uil uil-edit"></i>
                          </Label>
                        </div>

                        {imageFile && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 start-0 rounded-circle"
                            onClick={() => {
                              setImageFile(null);
                              setImagePreview(
                                data?.user?.imagePath
                                  ? `${API_ENDPOINTS.API_BASE_URL}${data.user.imagePath}`
                                  : userImage2
                              );
                            }}
                            style={{
                              width: "24px",
                              height: "24px",
                              padding: "0",
                            }}
                            title={
                              language === "pt"
                                ? "Remover imagem"
                                : "Remove image"
                            }
                          >
                            ×
                          </button>
                        )}
                      </div> */}
                    </div>
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <label htmlFor="firstName" className="form-label">
                            {language === "pt" ? "Nome *" : "Name *"}
                          </label>
                          <Input
                            disabled={isLoading}
                            type="text"
                            className="form-control"
                            id="firstName"
                            value={formData.firstName || ""}
                            invalid={shouldShowFieldError("firstName")}
                            onBlur={() => handleFieldBlur("firstName")}
                            onChange={(e) =>
                              handleFieldChange("firstName", e.target.value)
                            }
                          />
                          {shouldShowFieldError("firstName") && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.firstName}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="lastName" className="form-label">
                            {language === "pt" ? "Sobrenome *" : "Last Name *"}
                          </Label>
                          <Input
                            disabled={isLoading}
                            type="text"
                            className="form-control"
                            id="lastName"
                            value={formData.lastName || ""}
                            invalid={shouldShowFieldError("lastName")}
                            onBlur={() => handleFieldBlur("lastName")}
                            onChange={(e) =>
                              handleFieldChange("lastName", e.target.value)
                            }
                          />
                          {shouldShowFieldError("lastName") && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.lastName}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="phone" className="form-label">
                            {language === "pt" ? "Telefone" : "Phone"}
                          </Label>
                          <Input
                            disabled={isLoading}
                            type="tel"
                            className="form-control"
                            id="phone"
                            value={formData.phone || ""}
                            onChange={(e) => handleFieldChange("phone", e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="birthDate" className="form-label">
                            {language === "pt"
                              ? "Data de Nascimento *"
                              : "Birthdate *"}
                          </Label>
                          <Input
                            disabled={isLoading}
                            type="date"
                            className="form-control"
                            id="birthDate"
                            value={formData.birthDate || ""}
                            invalid={shouldShowFieldError("birthDate")}
                            onBlur={() => handleFieldBlur("birthDate")}
                            onChange={(e) =>
                              handleFieldChange("birthDate", e.target.value)
                            }
                          />
                          {shouldShowFieldError("birthDate") && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.birthDate}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="address" className="form-label">
                            {language === "pt" ? "Endereço" : "Address"}
                          </Label>
                          <Input
                            disabled={isLoading}
                            type="text"
                            className="form-control"
                            id="address"
                            value={formData.address || ""}
                            onChange={(e) =>
                              handleFieldChange("address", e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            htmlFor="yearsOfExperience"
                            className="form-label"
                          >
                            {language === "pt"
                              ? "Anos de Experiência *"
                              : "Years of Experience *"}
                          </Label>
                          <Input
                            disabled={isLoading}
                            type="number"
                            className="form-control"
                            id="yearsOfExperience"
                            value={formData.yearsOfExperience || ""}
                            invalid={shouldShowFieldError("yearsOfExperience")}
                            onBlur={() => handleFieldBlur("yearsOfExperience")}
                            onChange={(e) =>
                              handleFieldChange(
                                "yearsOfExperience",
                                e.target.value
                              )
                            }
                          />
                          {shouldShowFieldError("yearsOfExperience") && (
                            <div className="invalid-feedback d-block danger">
                              {fieldErrors.yearsOfExperience}
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <label htmlFor="gender" className="form-label">
                            {language === "pt" ? "Gênero *" : "Gender *"}
                          </label>
                          <Input
                            disabled={isLoading}
                            required
                            type="select"
                            className="form-control"
                            id="gender"
                            value={formData.gender || ""}
                            invalid={shouldShowFieldError("gender")}
                            onBlur={() => handleFieldBlur("gender")}
                            onChange={(e) =>
                              handleFieldChange("gender", e.target.value)
                            }
                            aria-label={
                              language === "pt"
                                ? "Selecione um gênero"
                                : "Select gender"
                            }
                          >
                            <option value="">
                              {language === "pt"
                                ? "Selecione um gênero"
                                : "Select a gender"}
                            </option>
                            {genders.map((gender) => (
                              <option key={gender.value} value={gender.value}>
                                {language === "pt"
                                  ? gender.value === "MALE"
                                    ? "Masculino"
                                    : gender.value === "FEMALE"
                                    ? "Feminino"
                                    : "Não especificado"
                                  : gender.label}
                              </option>
                            ))}
                          </Input>
                          {shouldShowFieldError("gender") && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.gender}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <label
                            htmlFor="desiredJobCategory"
                            className="form-label"
                          >
                            {language === "pt"
                              ? "Categoria do Trabalho Desejado *"
                              : "Desired Job Category *"}
                          </label>

                          {isLoading ? (
                            <div className="text-muted">
                              {language === "pt"
                                ? "Carregando categorias..."
                                : "Loading categories..."}
                            </div>
                          ) : isError ? (
                            <div className="text-danger">
                              {language === "pt"
                                ? "Erro ao carregar categorias"
                                : "Error loading categories"}
                            </div>
                          ) : (
                            <Input
                              required
                              type="select"
                              className="form-control"
                              id="desiredJobCategory"
                              disabled={isLoading}
                              value={formData.desiredJobCategory || ""}
                              invalid={shouldShowFieldError("desiredJobCategory")}
                              onBlur={() => handleFieldBlur("desiredJobCategory")}
                              onChange={(e) =>
                                handleFieldChange(
                                  "desiredJobCategory",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">
                                {language === "pt"
                                  ? "Selecione uma categoria"
                                  : "Select a category"}
                              </option>
                              {categoriesData?.content
                                ?.slice()
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((category) => (
                                  <option
                                    key={category.code}
                                    value={category.name}
                                  >
                                    {category.name}
                                  </option>
                                ))}
                            </Input>
                          )}
                          {shouldShowFieldError("desiredJobCategory") && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.desiredJobCategory}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <LocationSelector
                      onLocationChange={handleLocationChange}
                      disabled={isLoading}
                      initialValues={{
                        country: formData.country,
                        state: formData.state,
                        city: formData.city,
                      }}
                    />
                    <Col>
                      <div className="mb-3">
                        <Label htmlFor="linkedin" className="form-label">
                          LinkedIn
                        </Label>
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            id="linkedin-prefix"
                          >
                            https://www.linkedin.com/in/
                          </span>
                          <Input
                            disabled={isLoading}
                            type="text"
                            className="form-control"
                            id="linkedin"
                            placeholder="seu-username"
                            value={
                              formData.linkedin?.replace(
                                "https://www.linkedin.com/in/",
                                ""
                              ) || ""
                            }
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                linkedin: `https://www.linkedin.com/in/${e.target.value.replace(
                                  /^https:\/\/www\.linkedin\.com\/in\//,
                                  ""
                                )}`,
                              })
                            }
                          />
                        </div>
                        <small className="text-muted d-block mt-2">
                          {language === "pt"
                            ? 'Apenas o nome de usuário após "linkedin.com/in/".'
                            : 'Only the username after "linkedin.com/in/".'}
                        </small>
                      </div>
                    </Col>
                    <Row className={profileInsights.sectionStatus.find((s) => s.key === "summary")?.complete ? "" : "border border-warning rounded p-2 mx-0 mb-2"}>
                      <Col lg={12}>
                        <div className="mb-3">
                          <Label
                            htmlFor="professionalSummary"
                            className="form-label"
                          >
                            {language === "pt"
                              ? "Resumo Profissional"
                              : "Professional Summary"}
                          </Label>
                          <Input
                            type="textarea"
                            disabled={isLoading}
                            className="form-control"
                            rows="5"
                            id="professionalSummary"
                            value={formData.professionalSummary || ""}
                            onChange={(e) =>
                              handleFieldChange(
                                "professionalSummary",
                                e.target.value
                              )
                            }
                          />
                          <small className="text-muted d-block mt-2">
                            {language === "pt"
                              ? "Recomendado: pelo menos 80 caracteres para um resumo forte."
                              : "Recommended: at least 80 characters for a strong summary."}
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className={`mt-4 p-3 rounded border ${profileInsights.sectionStatus.find((s) => s.key === "documents")?.complete ? "" : "border-warning"}`}>
                    <h5 className="fs-17 fw-semibold mb-3">
                      {language === "pt" ? "Currículos" : "Resumes"}
                    </h5>

                    <div className="mb-3 border p-3 rounded">
                      <Row>
                        <Col md={5}>
                          <Label htmlFor="cvLanguage" className="form-label">
                            {language === "pt" ? "Idioma do CV" : "CV Language"}{" "}
                            *
                          </Label>
                          <Input
                            type="select"
                            disabled={isLoading}
                            className="form-control"
                            id="cvLanguage"
                            value={formData.cvLanguage || "pt"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                cvLanguage: e.target.value,
                              })
                            }
                          >
                            <option value="pt">
                              {language === "pt" ? "Português" : "Portuguese"}
                            </option>
                            <option value="en">
                              {language === "pt" ? "Inglês" : "English"}
                            </option>
                          </Input>

                          {formData.cvs &&
                            formData.cvs.some(
                              (cv) => cv.language === formData.cvLanguage
                            ) && (
                              <div className="mt-2">
                                <span className="text-warning small">
                                  <i className="uil uil-exclamation-circle me-1"></i>
                                  {language === "pt"
                                    ? `Novo upload substituirá o CV existente`
                                    : `New upload will replace existing CV`}
                                </span>
                              </div>
                            )}
                        </Col>

                        <Col>
                          <Label>
                            {language === "pt" ? "Arquivo do CV *" : "CV File *"}
                          </Label>
                          <Input
                            disabled={isLoading}
                            required={!formData.cvs?.length}
                            className="form-control"
                            type="file"
                            id="attachmentscv"
                            onChange={handleCvChange}
                            onBlur={() => setTouchedFields((prev) => ({ ...prev, cvFile: true }))}
                            accept=".pdf,.doc,.docx"
                          />
                          <small className="text-muted d-block mt-2">
                            {language === "pt"
                              ? formData.cvs?.some(
                                  (cv) => cv.language === formData.cvLanguage
                                )
                                ? `Substituir CV em ${
                                    formData.cvLanguage === "pt"
                                      ? "Português"
                                      : "Inglês"
                                  }`
                                : `Adicionar CV em ${
                                    formData.cvLanguage === "pt"
                                      ? "Português"
                                      : "Inglês"
                                  }`
                              : formData.cvs?.some(
                                  (cv) => cv.language === formData.cvLanguage
                                )
                              ? `Replace ${
                                  formData.cvLanguage === "pt"
                                    ? "Portuguese"
                                    : "English"
                                } CV`
                              : `Add ${
                                  formData.cvLanguage === "pt"
                                    ? "Portuguese"
                                    : "English"
                                } CV`}
                          </small>
                        </Col>
                      </Row>

                      {(submitAttempted || touchedFields.cvFile) && fieldErrors.cvFile && (
                        <div className="invalid-feedback d-block mt-2">
                          {fieldErrors.cvFile}
                        </div>
                      )}
                    </div>

                    {/* Current CVs section */}
                    {formData.cvs && formData.cvs.length > 0 && (
                      <div className="mt-3">
                        <h6 className="fs-14 fw-semibold mb-2">
                          {language === "pt"
                            ? "Currículos Atuais"
                            : "Current Resumes"}
                        </h6>
                        <ul className="list-unstyled">
                          {formData.cvs.map((cv, index) => (
                            <li
                              key={index}
                              className="mb-2 d-flex align-items-center"
                            >
                              <i className="uil uil-file-alt me-2"></i>
                              <span>
                                {cv.filePath.split("/").pop()} (
                                {cv.language === "pt"
                                  ? language === "pt"
                                    ? "Português"
                                    : "Portuguese"
                                  : language === "pt"
                                  ? "Inglês"
                                  : "English"}
                                )
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <small className="text-muted d-block mt-2">
                      {language === "pt"
                        ? "* Pelo menos um currículo é obrigatório (Formatos suportados: PDF, DOC, DOCX)"
                        : "* At least one resume is required (Supported formats: PDF, DOC, DOCX)"}
                    </small>
                  </div>

                  <div className="mt-4">
                    <h5 className="fs-17 fw-semibold mb-3">
                      {language === "pt" ? "Educação" : "Education"}
                    </h5>
                    {formData.educations?.map((education, index) => (
                      <div
                        key={index}
                        className="mb-4 p-3 border rounded position-relative"
                      >
                        {index > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 cursor-pointer"
                            disabled={isLoading}
                            onClick={() => handleRemoveEducation(index)}
                          >
                            ×
                          </button>
                        )}

                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-course-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Curso" : "Course"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`education-course-${index}`}
                                value={education.course || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "course",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-institution-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Instituição"
                                  : "Institution"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`education-institution-${index}`}
                                value={education.institution || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "institution",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-degree-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Grau" : "Degree"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`education-degree-${index}`}
                                value={education.degree || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "degree",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-location-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Localização" : "Location"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`education-location-${index}`}
                                value={education.location || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "location",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-startDate-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Data de Início"
                                  : "Start Date"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="date"
                                className="form-control"
                                id={`education-startDate-${index}`}
                                value={education.startDate || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "startDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-endDate-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Data de Término"
                                  : "End Date"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="date"
                                className="form-control"
                                id={`education-endDate-${index}`}
                                value={education.endDate || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "endDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`education-description-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Descrição"
                                  : "Description"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="textarea"
                                maxLength={255}
                                className="form-control"
                                id={`education-description-${index}`}
                                value={education.description || ""}
                                onChange={(e) =>
                                  handleEducationChange(
                                    index,
                                    "description",
                                    e.target.value
                                  )
                                }
                                rows="3"
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={isLoading}
                      onClick={handleAddEducation}
                    >
                      +{" "}
                      {language === "pt"
                        ? "Adicionar Educação"
                        : "Add Education"}
                    </button>
                  </div>

                  <div className={`mt-4 p-3 rounded border ${profileInsights.sectionStatus.find((s) => s.key === "experience")?.complete ? "" : "border-warning"}`}>
                    <h5 className="fs-17 fw-semibold mb-3">
                      {language === "pt"
                        ? "Experiência Profissional"
                        : "Professional Experience"}
                    </h5>
                    {formData.experiences?.map((experience, index) => (
                      <div
                        key={index}
                        className="mb-4 p-3 border rounded position-relative"
                      >
                        {index > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 cursor-pointer"
                            disabled={isLoading}
                            onClick={() => handleRemoveExperience(index)}
                          >
                            ×
                          </button>
                        )}

                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`experience-position-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Cargo" : "Position"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`experience-position-${index}`}
                                defaultValue={experience.position || ""}
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "position",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`experience-company-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Empresa" : "Company"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`experience-company-${index}`}
                                defaultValue={experience.company || ""}
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "company",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`experience-location-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Localização" : "Location"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`experience-location-${index}`}
                                defaultValue={experience.location || ""}
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "location",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`experience-startDate-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Data de Início"
                                  : "Start Date"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="date"
                                className="form-control"
                                id={`experience-startDate-${index}`}
                                defaultValue={experience.startDate || ""}
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "startDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`experience-endDate-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Data de Término"
                                  : "End Date"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="date"
                                className="form-control"
                                id={`experience-endDate-${index}`}
                                defaultValue={experience.endDate || ""}
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "endDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`experience-duties-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Atividades" : "Duties"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="textarea"
                                maxLength={255}
                                className="form-control"
                                id={`experience-duties-${index}`}
                                defaultValue={experience.duties || ""}
                                onChange={(e) =>
                                  handleExperienceChange(
                                    index,
                                    "duties",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={isLoading}
                      onClick={handleAddExperience}
                    >
                      +{" "}
                      {language === "pt"
                        ? "Adicionar Experiência"
                        : "Add Experience"}
                    </button>
                  </div>

                  <div className="mt-4">
                    <h5 className="fs-17 fw-semibold mb-3">
                      {language === "pt"
                        ? "Cursos e Certificações"
                        : "Courses and Certifications"}
                    </h5>
                    {formData.trainings?.map((training, index) => (
                      <div
                        key={index}
                        className="mb-4 p-3 border rounded position-relative"
                      >
                        {index > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 cursor-pointer"
                            onClick={() => handleRemoveTraining(index)}
                            disabled={isLoading}
                          >
                            ×
                          </button>
                        )}

                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`training-institution-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Instituição"
                                  : "Institution"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`training-institution-${index}`}
                                defaultValue={training.institution || ""}
                                onChange={(e) =>
                                  handleTrainingChange(
                                    index,
                                    "institution",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`training-course-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Curso" : "Course"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`training-course-${index}`}
                                defaultValue={training.course || ""}
                                onChange={(e) =>
                                  handleTrainingChange(
                                    index,
                                    "course",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`training-location-${index}`}
                                className="form-label"
                              >
                                {language === "pt" ? "Localização" : "Location"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="text"
                                className="form-control"
                                id={`training-location-${index}`}
                                defaultValue={training.location || ""}
                                onChange={(e) =>
                                  handleTrainingChange(
                                    index,
                                    "location",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`training-startDate-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Data de Início"
                                  : "Start Date"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="date"
                                className="form-control"
                                id={`training-startDate-${index}`}
                                defaultValue={training.startDate || ""}
                                onChange={(e) =>
                                  handleTrainingChange(
                                    index,
                                    "startDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`training-endDate-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Data de Término"
                                  : "End Date"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="date"
                                className="form-control"
                                id={`training-endDate-${index}`}
                                defaultValue={training.endDate || ""}
                                onChange={(e) =>
                                  handleTrainingChange(
                                    index,
                                    "endDate",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div className="mb-3">
                              <Label
                                htmlFor={`training-description-${index}`}
                                className="form-label"
                              >
                                {language === "pt"
                                  ? "Descrição"
                                  : "Description"}
                              </Label>
                              <Input
                                disabled={isLoading}
                                type="textarea"
                                maxLength={255}
                                className="form-control"
                                id={`training-description-${index}`}
                                defaultValue={training.description || ""}
                                onChange={(e) =>
                                  handleTrainingChange(
                                    index,
                                    "description",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      disabled={isLoading}
                      onClick={handleAddTraining}
                    >
                      +{" "}
                      {language === "pt"
                        ? "Adicionar Formação"
                        : "Add Training"}
                    </button>
                  </div>

                  <div className={`mt-4 p-3 rounded border ${profileInsights.sectionStatus.find((s) => s.key === "summary")?.complete ? "" : "border-warning"}`}>
                    <h5 className="fs-17 fw-semibold mb-3">
                      {language === "pt" ? "Habilidades" : "Skills"}
                    </h5>
                    <div className="mb-3">
                      <Label htmlFor="skills" className="form-label">
                        {language === "pt"
                          ? "Habilidades Profissionais"
                          : "Professional Skills"}
                      </Label>
                      <Input
                        disabled={isLoading}
                        type="text"
                        className="form-control"
                        id="skills"
                        value={skillsInput}
                        onChange={(e) => setSkillsInput(e.target.value)}
                        placeholder={
                          language === "pt"
                            ? "Ex: Spring Boot, Cybersecurity, Node.js"
                            : "Eg: Spring Boot, Cybersecurity, Node.js"
                        }
                        onBlur={() => {
                          setSkillsInput((prev) =>
                            prev
                              .split(",")
                              .map((skill) => skill.trim())
                              .filter((skill) => skill !== "")
                              .join(", ")
                          );
                        }}
                      />
                      <small className="text-muted d-block mt-2">
                        {language === "pt"
                          ? "Separe as habilidades por vírgula"
                          : "Separate skills with commas"}
                      </small>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h5 className="fs-17 fw-semibold mb-3">
                      {language === "pt" ? "Idiomas" : "Languages"}
                    </h5>

                    {formData.languages?.map((lang, index) => (
                      <div
                        key={lang.id || index}
                        className="mb-3 border p-3 rounded"
                      >
                        <Row>
                          <Col md={6}>
                            <Label>
                              {language === "pt" ? "Idioma" : "Language"} *
                            </Label>
                            <Input
                              required
                              disabled={isLoading}
                              value={lang.name}
                              invalid={Boolean((submitAttempted || touchedFields.languages) && fieldErrors.languages)}
                              onChange={(e) =>
                                handleLanguageChange(
                                  index,
                                  "name",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                setTouchedFields((prev) => ({ ...prev, languages: true }))
                              }
                              type="select"
                              className="form-control"
                              id={`language-${index}`}
                              aria-label={
                                language === "pt"
                                  ? "Selecione uma linguagem"
                                  : "Select a language"
                              }
                            >
                              <option value="">
                                {language === "pt"
                                  ? "Selecione uma linguagem"
                                  : "Select a language"}
                              </option>
                              {languages
                                .filter((languageOption) =>
                                  // Mostrar apenas idiomas não selecionados em outros campos OU o idioma atual deste campo
                                  formData.languages.every(
                                    (l, i) =>
                                      i === index ||
                                      l.name !== languageOption.value
                                  )
                                )
                                .map((languageOption) => (
                                  <option
                                    key={languageOption.value}
                                    value={languageOption.value}
                                  >
                                    {language === "pt"
                                      ? languageOption.labelPt
                                      : languageOption.label}
                                  </option>
                                ))}
                            </Input>
                          </Col>
                          <Col md={4}>
                            <Label>
                              {language === "pt" ? "Nível" : "Level"} *
                            </Label>
                            <Input
                              required
                              disabled={isLoading}
                              type="select"
                              value={lang.proficiencyLevel}
                              invalid={Boolean((submitAttempted || touchedFields.languages) && fieldErrors.languages)}
                              onChange={(e) =>
                                handleLanguageChange(
                                  index,
                                  "proficiencyLevel",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                setTouchedFields((prev) => ({ ...prev, languages: true }))
                              }
                            >
                              <option value="">
                                {language === "pt"
                                  ? "Selecione o nível"
                                  : "Select level"}
                              </option>
                              {Object.values(ProficiencyLevel).map((level) => (
                                <option key={level} value={level}>
                                  {language === "pt"
                                    ? getPortugueseProficiency(level)
                                    : level.charAt(0) +
                                      level.slice(1).toLowerCase()}
                                </option>
                              ))}
                            </Input>
                          </Col>
                          <Col md={2} className="d-flex align-items-end">
                            <Button
                              color="danger"
                              onClick={() => handleRemoveLanguage(index)}
                              disabled={
                                formData.languages.length <= 1 || isLoading
                              }
                            >
                              <i className="uil uil-trash-alt"></i>
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    ))}

                    <Button
                      color="outline-primary"
                      onClick={handleAddLanguage}
                      className="mt-2"
                      disabled={formData.languages.length >= languages.length}
                    >
                      <i className="uil uil-plus"></i>{" "}
                      {language === "pt" ? "Adicionar Idioma" : "Add Language"}
                    </Button>

                    {(submitAttempted || touchedFields.languages) &&
                      fieldErrors.languages && (
                        <div className="invalid-feedback d-block mt-2">
                          {fieldErrors.languages}
                        </div>
                      )}
                  </div>

                  <div className="mt-4 text-end">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {language === "pt" ? "Atualizando..." : "Updating..."}
                        </>
                      ) : language === "pt" ? (
                        "Atualizar Perfil"
                      ) : (
                        "Update Profile"
                      )}
                    </button>
                  </div>
                </Form>
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default RightSideContent;
